<script setup lang="ts">
  import { defineProps, onMounted, provide, ref, watch } from 'vue';
  import CalculatorNotes from '@/components/calculator/CalculatorNotes.vue';
  import InputCustom from '@/components/components/inputCustom.vue';
  import ButtonCustom from '@/components/components/buttonCustom.vue';
  import RadioBtnGroup from '@/components/components/radioBtnGroup.vue';
  import PageNavigation from '@/components/components/PageNavigation.vue';
  import SliderWithModal from '@/components/components/SliderWithModal.vue';
  import DealerInfo from '@/components/components/dealerInfo.vue';
  import DocumentInfo from '@/components/components/documentInfo.vue';
  import MainCalcBlock from '@/components/calculator/mainCalcBlock.vue';
  import TotalCalcBlock from '@/components/calculator/TotalCalcBlock.vue';
  import { useParser } from '@/store/parser';
  import {
    calcPensionCost,
    getFreeACopart,
    getFreeAIAAI,
    getLiveBindCopart,
    getLiveBindIAAI,
    localstorageGet,
    localstorageSet,
  } from '@/helpers/helper';
  import { additionalCosts, carData } from '@/types';
  import { getSession, saveCar } from '@/indexedDB';

  const props = defineProps<{
    carData: carData | null;
    additionalFields: additionalCosts | null;
  }>();

  const link = ref<string>(localstorageGet('link') ?? '');
  const activeTab = ref<number>(1);
  const loading = ref<boolean>(false);
  const additionalFields = ref<additionalCosts | null>(props.additionalFields);
  const dataCar = ref<carData | null>(props.carData);

  const auction = ref<string>(dataCar.value?.info?.countryCalc ?? 'poland');
  const { parseLink, getCustomClearance, getCurrencyCourse } = useParser();

  // Сохранение link в localStorage
  watch(link, (newLink) => {
    localstorageSet('link', newLink);
  });
  provide('link', link);

  async function updateAuctionFee() {
    const priceNow = +dataCar.value?.info?.priceNow || 0;
    if (link.value.includes('copart')) {
      dataCar.value.info.auctionFee =
        getLiveBindCopart(priceNow) + getFreeACopart(priceNow) + 95 + 35;
    } else {
      dataCar.value.info.auctionFee =
        getLiveBindIAAI(priceNow) + getFreeAIAAI(priceNow) + 140 + 25;
    }
    await saveCar('car', dataCar.value);
  }

  // Обработка клика "Scan"
  async function parseCar() {
    loading.value = true;
    dataCar.value = await parseLink(link.value, auction.value);
    // Устанавливаем значения по умолчанию
    dataCar.value.shipping.body = 'Crossover';
    dataCar.value.shipping.costShippingInsurance = 75;
    dataCar.value.info.countryCalc = auction.value;
    await updateAuctionFee();
    if (
      dataCar.value.info.countryCalc === 'ukraine' ||
      dataCar.value.info.countryCalc === 'poland'
    ) {
      additionalFields.value.customsCost = await getCustomClearance(
        auction.value,
        dataCar.value
      );
    }

    additionalFields.value.pensionCost = 0;
    if (dataCar.value.info.countryCalc === 'ukraine') {
      const course = await getCurrencyCourse();

      additionalFields.value.pensionCost = calcPensionCost(
        +dataCar.value.info.priceNow,
        +course,
        +dataCar.value.info.auctionFee
      );
    }

    if (dataCar.value?.info.countryCalc === 'poland') {
      additionalFields.value.parkingCost = 0;
      additionalFields.value.towingCost = 0;
    }

    if (dataCar.value?.info.countryCalc === 'georgy') {
      additionalFields.value.parkingCost = 0;
      additionalFields.value.towingCost = 0;
      additionalFields.value.expeditionCost = 0;
      additionalFields.value.certification = 0;
      additionalFields.value.customsCost = 0;
    }

    localstorageSet(
      'auctionName',
      link.value.includes('copart') ? 'copart' : 'iaai'
    );
    localstorageSet('readNews', false);
    activeTab.value = 2;
    await saveCar('car', dataCar.value);
    await saveCar('additionalFields', additionalFields.value);
    localStorage.removeItem('analytics');
    loading.value = false;
  }

  onMounted(async () => {
    const sessionCar = await getSession('car');
    if (sessionCar) dataCar.value = sessionCar;
    const sessionAdditional = await getSession('additionalFields');
    if (sessionAdditional) additionalFields.value = sessionAdditional;
  });
</script>

<template>
  <div class="h-full">
    <div v-if="Object.keys(dataCar?.info ?? {}).length > 1">
      <PageNavigation
        title="Pages"
        :totalPages="dataCar?.info?.countryCalc === 'georgy' ? 3 : 4"
        :activePage="activeTab"
        :key="activeTab"
        @update:currentPage="(page: number) => (activeTab = page)"
      />
    </div>
    <div>
      <div v-if="activeTab === 1">
        <div
          :class="{ 'mt-4': dataCar }"
          class="p-2 bg-white dark:border-gray-800 rounded-md shadow-md"
        >
          <input-custom v-model="link" />
        </div>
        <div
          class="mt-4 p-2 bg-white dark:border-gray-800 rounded-md shadow-md"
        >
          <radio-btn-group
            v-model="auction"
            :options="[
              { key: 'poland', label: 'Poland' },
              { key: 'georgy', label: 'George' },
              { key: 'ukraine', label: 'Ukraine' },
            ]"
          />
        </div>
        <div
          class="mt-2 p-1 bg-white dark:border-gray-800 rounded-md shadow-md"
        >
          <button-custom
            title="Scan"
            :loading="loading"
            :disable="!link || !auction"
            @click="parseCar"
          />
        </div>
      </div>
      <div v-if="activeTab === 2">
        <CalculatorNotes :country="dataCar.info.countryCalc" />
        <document-info :data="dataCar.info" />
        <dealer-info :data="dataCar.info" />
        <SliderWithModal
          :images="
            dataCar.img.filter((value) => value.imageTypeCode !== 'ENAUD')
          "
        />
      </div>
      <div v-if="activeTab === 3">
        <main-calc-block :key="activeTab" />
      </div>
      <div v-if="dataCar?.info?.countryCalc !== 'georgy' && activeTab === 4">
        <total-calc-block :key="activeTab" />
      </div>
    </div>
  </div>
</template>
